import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import {
  TenantApiError,
  type PaginationMeta,
  type TenantCategory,
  type TenantMenuItem,
} from "@qr-menu/api-tenant";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  RowActions,
  RowConfirm,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; items: TenantMenuItem[]; meta: PaginationMeta };

const NAME_MIN = 2;
const NAME_MAX = 150;
const DESCRIPTION_MAX = 1000;

// Matches what the backend's normalizeUnsignedDecimal(price, 12, 2) accepts:
// digits, at most one dot, no sign, no comma, at most two fraction digits.
const PRICE_PATTERN = /^\d+(\.\d{1,2})?$/;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;

// Composite key for a row's current image: item id plus its own URL. Keying on
// both means a fresh upsert (a new URL under the same item id) is never
// mistaken for still-broken just because an old URL under that id once failed.
function imageRowKey(itemId: number, imageUrl: string): string {
  return `${itemId}:${imageUrl}`;
}

const IMAGE_URL_MAX = 2048;
const ALT_TEXT_MAX = 300;
// Control characters: C0 range, DEL, and the C1 range. The backend rejects any
// of these in alt text; the URL additionally rejects all whitespace. The URL is
// never fetched here (no HEAD/GET/DNS/prefetch); it is only string-validated.
const CONTROL_PATTERN = /[\u0000-\u001f\u007f-\u009f]/;
const URL_FORBIDDEN_PATTERN = /[\s\u0000-\u001f\u007f-\u009f]/;

export function ProductsRoute() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [categories, setCategories] = useState<TenantCategory[]>([]);

  // editingId drives create-vs-edit; editingItem holds the full original record
  // so the fields the form does not expose (ingredients, allergens, nutrition)
  // can be sent back unchanged by the full-replacement PUT.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<TenantMenuItem | null>(null);

  const [categoryId, setCategoryId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [calories, setCalories] = useState("");
  const [sortOrder, setSortOrder] = useState("0");
  const [isActive, setIsActive] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Row image keys (see imageRowKey) whose thumbnail failed to load. A stored
  // URL can 404 long after it was saved, and a broken <img> would show the
  // browser's error icon; those rows fall back to a "Yüklenemedi" indicator.
  const [brokenImageKeys, setBrokenImageKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  function handleThumbError(key: string) {
    setBrokenImageKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }

  // The image form is a separate section; imageEditingItem holds the item whose
  // primary image URL/alt text is being edited (null when the form is closed).
  const [imageEditingItem, setImageEditingItem] =
    useState<TenantMenuItem | null>(null);
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [imageSubmitting, setImageSubmitting] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  // Two-stage removal of the current primary image, scoped to the one open
  // image form: pending shows the inline confirm, deleting marks it in flight.
  const [imageDeletePending, setImageDeletePending] = useState(false);
  const [imageDeleting, setImageDeleting] = useState(false);

  // One in-flight mutation at a time across create, update, delete, image
  // upsert and image delete: any of them blocks the others and every row action.
  const busy =
    submitting || deletingId !== null || imageSubmitting || imageDeleting;

  // Client-side only: filters the already-loaded page of products, never a
  // separate request. "" for filterCategoryId means every category.
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const searchTrimmed = searchQuery.trim();
  const hasActiveFilter = searchTrimmed !== "" || filterCategoryId !== "";

  function handleFilterReset() {
    setSearchQuery("");
    setFilterCategoryId("");
  }

  const filteredItems = useMemo(() => {
    if (state.kind !== "ready") return [];
    // toLocaleLowerCase("tr") (not toLowerCase()) so "İ"/"I" fold the way a
    // Turkish keyboard expects, with no normalization dependency beyond the
    // engine's built-in Intl support.
    const needle = searchTrimmed.toLocaleLowerCase("tr");
    return state.items.filter((item) => {
      if (
        filterCategoryId !== "" &&
        String(item.category_id) !== filterCategoryId
      ) {
        return false;
      }
      if (needle === "") return true;
      return (
        item.name.toLocaleLowerCase("tr").includes(needle) ||
        item.description.toLocaleLowerCase("tr").includes(needle)
      );
    });
  }, [state, searchTrimmed, filterCategoryId]);

  const loadItems = useCallback(async () => {
    try {
      const res = await tenantApi.menuItems({ limit: 50, offset: 0 });
      setState({ kind: "ready", items: res.items, meta: res.meta });
    } catch (err) {
      setState({ kind: "error", message: describe(err, "Ürünler alınamadı.") });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      await loadItems();

      try {
        const res = await tenantApi.categories({
          includeInactive: false,
          limit: 50,
          offset: 0,
        });
        if (cancelled) return;
        setCategories(res.items);
        const first = res.items[0];
        if (first) {
          setCategoryId(String(first.id));
        }
      } catch {
        // A category load failure only disables creation; the product list
        // above stays usable, and the empty-category notice covers the UI.
        if (!cancelled) setCategories([]);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [loadItems]);

  const firstCategoryId = categories[0]?.id ?? null;
  const hasCategories = categories.length > 0;

  function resetForm() {
    setCategoryId(firstCategoryId == null ? "" : String(firstCategoryId));
    setName("");
    setDescription("");
    setPrice("");
    setCurrency("TRY");
    setCalories("");
    setSortOrder("0");
    setIsActive(true);
  }

  function handleEdit(row: TenantMenuItem) {
    // Only the visible fields are copied into the form inputs; the row itself is
    // kept in editingItem so the hidden fields survive the update untouched.
    setEditingId(row.id);
    setEditingItem(row);
    setCategoryId(String(row.category_id));
    setName(row.name);
    setDescription(row.description);
    setPrice(row.price);
    setCurrency(row.currency);
    setCalories(row.calories == null ? "" : String(row.calories));
    setSortOrder(String(row.sort_order));
    setIsActive(row.is_active);
    setFormError(null);
    setSuccessMessage(null);
  }

  function handleCancel() {
    // No request: just drop edit mode and return the form to its create state.
    setEditingId(null);
    setEditingItem(null);
    resetForm();
    setFormError(null);
    setSuccessMessage(null);
  }

  function handleDeleteRequest(id: number) {
    // Moves the pending selection to this row; no request is sent yet. Editing
    // is left untouched: the button for the row being edited is disabled, so a
    // pending selection can only belong to some other row.
    setPendingDeleteId(id);
    setDeleteError(null);
    setSuccessMessage(null);
  }

  function handleDeleteCancel() {
    setPendingDeleteId(null);
    setDeleteError(null);
  }

  async function handleDeleteConfirm(id: number) {
    if (busy) return;

    setDeletingId(id);
    setDeleteError(null);
    setSuccessMessage(null);
    try {
      await tenantApi.deleteMenuItem(id);
      setPendingDeleteId(null);
      // Refetch rather than removing the row locally, so the list (and the
      // empty state when the last item goes) reflects the server.
      await loadItems();
      setSuccessMessage("Ürün silindi.");
    } catch (err) {
      // A 401 already triggered the client's onUnauthorized. Every other
      // failure keeps the row, keeps the pending selection so the user can
      // retry or back out, and shows the backend's safe public message.
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setDeleteError(describe(err, "Ürün silinemedi."));
      }
    } finally {
      setDeletingId(null);
    }
  }

  function handleImageEdit(row: TenantMenuItem) {
    // Prefill straight from the list row, which already carries image_url and
    // image_alt_text; no extra fetch. Product edit state is left untouched.
    setImageEditingItem(row);
    setImageUrl(row.image_url);
    setImageAlt(row.image_alt_text);
    setImageError(null);
    // Opening the form for another item drops any stale remove confirmation.
    setImageDeletePending(false);
    setFormError(null);
    setSuccessMessage(null);
    setDeleteError(null);
  }

  function handleImageCancel() {
    // No request: just close the image form and drop its state, including any
    // pending remove confirmation.
    setImageEditingItem(null);
    setImageUrl("");
    setImageAlt("");
    setImageError(null);
    setImageDeletePending(false);
  }

  function handleImageDeleteRequest() {
    // First click only arms the inline confirm; no request is sent.
    setImageDeletePending(true);
    setImageError(null);
    setSuccessMessage(null);
  }

  function handleImageDeleteCancel() {
    setImageDeletePending(false);
    setImageError(null);
  }

  async function handleImageDeleteConfirm() {
    const item = imageEditingItem;
    if (busy || item === null) return;

    setImageError(null);
    setFormError(null);
    setSuccessMessage(null);
    setDeleteError(null);
    setImageDeleting(true);
    try {
      await tenantApi.deleteMenuItemImage(item.id);
      setImageDeletePending(false);
      setImageEditingItem(null);
      setImageUrl("");
      setImageAlt("");
      // Refetch: the Görsel indicator must flip Var -> Yok from the server, not
      // be patched in locally.
      await loadItems();
      setSuccessMessage("Ürün görseli kaldırıldı.");
    } catch (err) {
      // A 401 already triggered the client's onUnauthorized. Every other
      // failure keeps the form open with its values, keeps the pending confirm
      // so the user can retry or back out, and leaves the list untouched.
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setImageError(describe(err, "Ürün görseli kaldırılamadı."));
      }
    } finally {
      setImageDeleting(false);
    }
  }

  async function handleImageSubmit(e: FormEvent) {
    e.preventDefault();
    const item = imageEditingItem;
    if (busy || item === null) return;

    // Clear every stale message so an old product/delete/image line can never
    // sit next to a fresh image validation error.
    setImageError(null);
    setFormError(null);
    setSuccessMessage(null);
    setDeleteError(null);

    // URL policy mirrors the backend's normalizeMenuItemImageURL: https-only,
    // absolute, host present, no credentials, no fragment, no whitespace or
    // control characters. The URL is never fetched, only string-validated.
    const trimmedUrl = imageUrl.trim();
    if (trimmedUrl === "") {
      setImageError("Görsel URL zorunludur.");
      return;
    }
    if ([...trimmedUrl].length > IMAGE_URL_MAX) {
      setImageError(`Görsel URL en fazla ${IMAGE_URL_MAX} karakter olabilir.`);
      return;
    }
    if (URL_FORBIDDEN_PATTERN.test(trimmedUrl)) {
      setImageError("Görsel URL boşluk veya kontrol karakteri içeremez.");
      return;
    }
    let parsedUrl: URL;
    try {
      parsedUrl = new URL(trimmedUrl);
    } catch {
      setImageError("Görsel URL geçerli bir adres olmalıdır.");
      return;
    }
    if (parsedUrl.protocol !== "https:") {
      setImageError("Görsel URL https:// ile başlamalıdır.");
      return;
    }
    if (parsedUrl.hostname === "") {
      setImageError("Görsel URL geçerli bir alan adı içermelidir.");
      return;
    }
    if (parsedUrl.username !== "" || parsedUrl.password !== "") {
      setImageError("Görsel URL kullanıcı adı veya parola içeremez.");
      return;
    }
    if (parsedUrl.hash !== "") {
      setImageError("Görsel URL # (fragment) içeremez.");
      return;
    }

    // Alt text is optional; only length and control characters are checked.
    const trimmedAlt = imageAlt.trim();
    if ([...trimmedAlt].length > ALT_TEXT_MAX) {
      setImageError(`Alt metin en fazla ${ALT_TEXT_MAX} karakter olabilir.`);
      return;
    }
    if (CONTROL_PATTERN.test(trimmedAlt)) {
      setImageError("Alt metin kontrol karakteri içeremez.");
      return;
    }

    setImageSubmitting(true);
    try {
      // Send the trimmed original string; the backend stores it verbatim.
      await tenantApi.upsertMenuItemImage(item.id, {
        image_url: trimmedUrl,
        alt_text: trimmedAlt,
      });
      setImageEditingItem(null);
      setImageUrl("");
      setImageAlt("");
      setImageDeletePending(false);
      // Refetch: the list row's image_url/image_alt_text (and the Görsel
      // indicator) must come from the server, not be patched in locally.
      await loadItems();
      setSuccessMessage("Ürün görseli güncellendi.");
    } catch (err) {
      // A 401 already triggered the client's onUnauthorized. Every other
      // failure keeps the image form open with its values and the current list
      // untouched, showing the backend's safe public message.
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setImageError(describe(err, "Ürün görseli güncellenemedi."));
      }
    } finally {
      setImageSubmitting(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const isEditing = editingId !== null;
    // A create needs at least one active category; an edit may still be
    // cancelled or resubmitted even if the active-category list is empty. The
    // busy guard also blocks submission while a delete is in flight.
    if (busy || (!isEditing && !hasCategories)) return;

    // Cleared before validation, not after it: an early return below must not
    // leave the previous attempt's success message on screen next to a fresh
    // validation error.
    setFormError(null);
    setSuccessMessage(null);

    const parsedCategoryId = Number(categoryId);
    if (!Number.isInteger(parsedCategoryId) || parsedCategoryId <= 0) {
      setFormError("Kategori seçilmelidir.");
      return;
    }

    // In edit mode the item's original category can be inactive, in which case
    // it is not in the active list and cannot be reused. Rather than silently
    // sending an unusable category, block the update until an active one is
    // picked.
    if (isEditing && !categories.some((c) => c.id === parsedCategoryId)) {
      setFormError(
        "Mevcut kategori aktif değil. Güncellemek için aktif bir kategori seçin.",
      );
      return;
    }

    const trimmedName = name.trim();
    if (trimmedName.length < NAME_MIN || trimmedName.length > NAME_MAX) {
      setFormError(
        `Ürün adı en az ${NAME_MIN}, en fazla ${NAME_MAX} karakter olmalıdır.`,
      );
      return;
    }

    const trimmedDescription = description.trim();
    if (trimmedDescription.length > DESCRIPTION_MAX) {
      setFormError(`Açıklama en fazla ${DESCRIPTION_MAX} karakter olabilir.`);
      return;
    }

    // Kept as a trimmed string: converting to a JS number would lose money
    // precision, and the backend normalizes the decimal itself.
    const trimmedPrice = price.trim();
    if (trimmedPrice === "") {
      setFormError("Fiyat zorunludur.");
      return;
    }
    if (!PRICE_PATTERN.test(trimmedPrice)) {
      setFormError(
        "Fiyat 120, 120.5 veya 120.50 biçiminde olmalıdır (virgül veya eksi kullanılamaz).",
      );
      return;
    }

    const normalizedCurrency = currency.trim().toUpperCase();
    if (!CURRENCY_PATTERN.test(normalizedCurrency)) {
      setFormError("Para birimi üç harfli olmalıdır (örn. TRY).");
      return;
    }

    let parsedCalories: number | null = null;
    const trimmedCalories = calories.trim();
    if (trimmedCalories !== "") {
      const n = Number(trimmedCalories);
      if (!Number.isInteger(n) || n < 0) {
        setFormError("Kalori negatif olmayan bir tam sayı olmalıdır.");
        return;
      }
      parsedCalories = n;
    }

    const parsedSort = Number(sortOrder);
    if (!Number.isInteger(parsedSort) || parsedSort < 0) {
      setFormError("Sıra negatif olmayan bir tam sayı olmalıdır.");
      return;
    }

    // The visible fields shared by both flows; the hidden fields differ and are
    // filled in per flow below.
    const visible = {
      category_id: parsedCategoryId,
      name: trimmedName,
      description: trimmedDescription,
      price: trimmedPrice,
      currency: normalizedCurrency,
      calories: parsedCalories,
      sort_order: parsedSort,
      is_active: isActive,
    };

    const original = editingItem;

    setSubmitting(true);
    try {
      if (editingId === null) {
        await tenantApi.createMenuItem({
          ...visible,
          ingredients: "",
          allergens: [],
          protein: null,
          carbs: null,
          fat: null,
          weight_grams: null,
        });
        resetForm();
        // Refetch: the backend normalizes price ("120" becomes "120.00"),
        // currency and ordering, so the row must come from the server.
        await loadItems();
        setSuccessMessage("Ürün oluşturuldu.");
      } else if (original !== null) {
        // PUT is a full replacement, so the fields the form does not expose are
        // carried back verbatim from the original record instead of being reset
        // to the create defaults.
        await tenantApi.updateMenuItem(editingId, {
          ...visible,
          ingredients: original.ingredients,
          allergens: original.allergens,
          protein: original.protein,
          carbs: original.carbs,
          fat: original.fat,
          weight_grams: original.weight_grams,
        });
        setEditingId(null);
        setEditingItem(null);
        resetForm();
        await loadItems();
        setSuccessMessage("Ürün güncellendi.");
      }
    } catch (err) {
      // A 401 already triggered the client's onUnauthorized. Every other
      // failure keeps the form values, the current list and (in edit mode) edit
      // mode itself untouched, and no refetch is issued.
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setFormError(
          describe(
            err,
            editingId === null
              ? "Ürün oluşturulamadı."
              : "Ürün güncellenemedi.",
          ),
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  const isEditing = editingId !== null;
  // In edit mode the original category can be inactive and therefore absent from
  // the active-category select; this surfaces a notice instead of a fake option.
  const editingCategoryInactive =
    isEditing && !categories.some((c) => String(c.id) === categoryId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ürünler</CardTitle>
        <CardDescription>Aktif işletmenin menü ürünleri.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="create-form" onSubmit={handleSubmit}>
          <p className="form-heading">
            {isEditing ? `Ürün düzenleniyor (#${editingId})` : "Yeni ürün"}
          </p>
          {!isEditing && !hasCategories && (
            <div className="state">
              Ürün oluşturmak için önce aktif bir kategori oluşturun.
            </div>
          )}
          <div className="create-form-grid">
            <Field label="Kategori" htmlFor="product-category">
              <select
                id="product-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={busy || !hasCategories}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {editingCategoryInactive && (
                <span className="hint">
                  Mevcut kategori aktif değil. Güncellemek için aktif bir kategori
                  seçin.
                </span>
              )}
            </Field>
            <Field label="Ürün adı" htmlFor="product-name">
              <input
                id="product-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={NAME_MAX}
                placeholder="Mercimek çorbası"
                disabled={busy}
              />
            </Field>
            <Field label="Açıklama" htmlFor="product-description">
              <input
                id="product-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={DESCRIPTION_MAX}
                placeholder="Opsiyonel"
                disabled={busy}
              />
            </Field>
            <Field label="Fiyat" htmlFor="product-price">
              <input
                id="product-price"
                type="text"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="120.00"
                disabled={busy}
              />
            </Field>
            <Field label="Para birimi" htmlFor="product-currency">
              <input
                id="product-currency"
                type="text"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                maxLength={3}
                placeholder="TRY"
                disabled={busy}
              />
            </Field>
            <Field label="Kalori" htmlFor="product-calories">
              <input
                id="product-calories"
                type="number"
                min={0}
                step={1}
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
                placeholder="Opsiyonel"
                disabled={busy}
              />
            </Field>
            <Field label="Sıra" htmlFor="product-sort-order">
              <input
                id="product-sort-order"
                type="number"
                min={0}
                step={1}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                disabled={busy}
              />
            </Field>
            <Field label="Aktif" htmlFor="product-active" variant="checkbox">
              <input
                id="product-active"
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={busy}
              />
            </Field>
            <div className="filter-actions">
              <button
                type="submit"
                className="primary"
                disabled={busy || (!isEditing && !hasCategories)}
              >
                {submitting
                  ? "Kaydediliyor…"
                  : isEditing
                    ? "Ürünü güncelle"
                    : "Yeni ürün"}
              </button>
              {isEditing && (
                <button type="button" onClick={handleCancel} disabled={busy}>
                  İptal
                </button>
              )}
            </div>
          </div>
          {formError && <div className="state error">{formError}</div>}
          {successMessage && <div className="state ok">{successMessage}</div>}
        </form>

        {imageEditingItem !== null && (
          <form className="create-form" onSubmit={handleImageSubmit}>
            <p className="form-heading">
              Ürün görseli (#{imageEditingItem.id})
            </p>
            <div className="create-form-grid">
              <Field label="Görsel URL" htmlFor="product-image-url">
                <input
                  id="product-image-url"
                  type="text"
                  inputMode="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  maxLength={IMAGE_URL_MAX}
                  placeholder="https://example.com/menu/urun.jpg"
                  disabled={busy}
                />
              </Field>
              <Field label="Alt metin" htmlFor="product-image-alt">
                <input
                  id="product-image-alt"
                  type="text"
                  value={imageAlt}
                  onChange={(e) => setImageAlt(e.target.value)}
                  maxLength={ALT_TEXT_MAX}
                  placeholder="Opsiyonel"
                  disabled={busy}
                />
              </Field>
              <div className="filter-actions">
                <button type="submit" className="primary" disabled={busy}>
                  {imageSubmitting ? "Kaydediliyor…" : "Görseli kaydet"}
                </button>
                <button type="button" onClick={handleImageCancel} disabled={busy}>
                  İptal
                </button>
              </div>
            </div>
            {imageEditingItem.image_url !== "" && (
              <RowActions style={{ marginTop: 8 }}>
                {imageDeletePending ? (
                  <RowConfirm>
                    <span className="hint">Kaldırılsın mı?</span>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={handleImageDeleteConfirm}
                      disabled={busy}
                    >
                      {imageDeleting ? "Kaldırılıyor…" : "Evet, kaldır"}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleImageDeleteCancel}
                      disabled={busy}
                    >
                      Vazgeç
                    </Button>
                  </RowConfirm>
                ) : (
                  <RowConfirm>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={handleImageDeleteRequest}
                      disabled={busy}
                    >
                      Görseli kaldır
                    </Button>
                  </RowConfirm>
                )}
              </RowActions>
            )}
            {imageError && <div className="state error">{imageError}</div>}
          </form>
        )}

        <div className="create-form">
          <p className="form-heading">Ürünlerde ara</p>
          <div className="create-form-grid">
            <Field label="Arama" htmlFor="product-search">
              <input
                id="product-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Ürün adı veya açıklama ara"
              />
            </Field>
            <Field label="Kategori" htmlFor="product-filter-category">
              <select
                id="product-filter-category"
                value={filterCategoryId}
                onChange={(e) => setFilterCategoryId(e.target.value)}
              >
                <option value="">Tüm kategoriler</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="filter-actions">
              <button
                type="button"
                onClick={handleFilterReset}
                disabled={!hasActiveFilter}
              >
                Filtreyi temizle
              </button>
            </div>
          </div>
        </div>

        {state.kind === "loading" && <div className="state">Yükleniyor…</div>}

        {state.kind === "error" && (
          <div className="state error">Yüklenemedi. {state.message}</div>
        )}

        {state.kind === "ready" && (
          <ProductsBody
            items={filteredItems}
            totalCount={state.meta.total}
            hasActiveFilter={hasActiveFilter}
            editingId={editingId}
            imageEditingId={imageEditingItem?.id ?? null}
            actionsDisabled={busy}
            pendingDeleteId={pendingDeleteId}
            deletingId={deletingId}
            deleteError={deleteError}
            brokenImageKeys={brokenImageKeys}
            onThumbError={handleThumbError}
            onEdit={handleEdit}
            onImageEdit={handleImageEdit}
            onDeleteRequest={handleDeleteRequest}
            onDeleteConfirm={handleDeleteConfirm}
            onDeleteCancel={handleDeleteCancel}
          />
        )}
      </CardContent>
    </Card>
  );
}

interface ProductsBodyProps {
  items: TenantMenuItem[];
  totalCount: number;
  hasActiveFilter: boolean;
  editingId: number | null;
  imageEditingId: number | null;
  actionsDisabled: boolean;
  pendingDeleteId: number | null;
  deletingId: number | null;
  deleteError: string | null;
  brokenImageKeys: ReadonlySet<string>;
  onThumbError: (key: string) => void;
  onEdit: (row: TenantMenuItem) => void;
  onImageEdit: (row: TenantMenuItem) => void;
  onDeleteRequest: (id: number) => void;
  onDeleteConfirm: (id: number) => void;
  onDeleteCancel: () => void;
}

function ProductsBody({
  items,
  totalCount,
  hasActiveFilter,
  editingId,
  imageEditingId,
  actionsDisabled,
  pendingDeleteId,
  deletingId,
  deleteError,
  brokenImageKeys,
  onThumbError,
  onEdit,
  onImageEdit,
  onDeleteRequest,
  onDeleteConfirm,
  onDeleteCancel,
}: ProductsBodyProps) {
  // Cross-mode locks that hold even when no request is in flight: editing a
  // product's fields locks the image buttons, and having the image form open
  // locks the row edit/delete buttons.
  const productEditOpen = editingId !== null;
  const imageEditOpen = imageEditingId !== null;
  if (items.length === 0) {
    return (
      <div className="state">
        {hasActiveFilter
          ? "Filtreyle eşleşen ürün yok."
          : "Henüz ürün bulunmuyor."}
      </div>
    );
  }

  return (
    <>
      {deleteError && <div className="state error">{deleteError}</div>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableCell as="th">Ürün</TableCell>
            <TableCell as="th" style={{ width: 110 }}>
              Kategori
            </TableCell>
            <TableCell as="th" style={{ width: 140 }}>
              Fiyat
            </TableCell>
            <TableCell as="th" style={{ width: 90 }}>
              Kalori
            </TableCell>
            <TableCell as="th" style={{ width: 80 }}>
              Sıra
            </TableCell>
            <TableCell as="th" style={{ width: 100 }}>
              Durum
            </TableCell>
            <TableCell as="th" style={{ width: 110 }}>
              Görsel
            </TableCell>
            <TableCell as="th" style={{ width: 260 }}>
              İşlem
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="wrap-cell">{row.name}</TableCell>
              <TableCell>
                <code>#{row.category_id}</code>
              </TableCell>
              <TableCell>
                {row.price} {row.currency}
              </TableCell>
              <TableCell>
                {row.calories == null ? (
                  <span className="hint">—</span>
                ) : (
                  row.calories
                )}
              </TableCell>
              <TableCell>{row.sort_order}</TableCell>
              <TableCell>
                <Badge variant={row.is_active ? "success" : "danger"}>
                  {row.is_active ? "Aktif" : "Pasif"}
                </Badge>
              </TableCell>
              <TableCell>
                <ProductImageCell
                  item={row}
                  broken={brokenImageKeys.has(
                    imageRowKey(row.id, row.image_url),
                  )}
                  onError={() =>
                    onThumbError(imageRowKey(row.id, row.image_url))
                  }
                />
              </TableCell>
              <RowActions as="td">
                {pendingDeleteId === row.id ? (
                  <RowConfirm>
                    <span className="hint">Silinsin mi?</span>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => onDeleteConfirm(row.id)}
                      disabled={actionsDisabled}
                    >
                      {deletingId === row.id ? "Siliniyor…" : "Evet, sil"}
                    </Button>
                    <Button
                      type="button"
                      onClick={onDeleteCancel}
                      disabled={actionsDisabled}
                    >
                      Vazgeç
                    </Button>
                  </RowConfirm>
                ) : (
                  <RowConfirm>
                    <Button
                      type="button"
                      onClick={() => onEdit(row)}
                      disabled={actionsDisabled || imageEditOpen}
                    >
                      {editingId === row.id ? "Düzenleniyor" : "Düzenle"}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => onImageEdit(row)}
                      disabled={actionsDisabled || productEditOpen || imageEditOpen}
                    >
                      {imageEditingId === row.id ? "Görsel açık" : "Görsel"}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => onDeleteRequest(row.id)}
                      disabled={
                        actionsDisabled || editingId === row.id || imageEditOpen
                      }
                    >
                      Sil
                    </Button>
                  </RowConfirm>
                )}
              </RowActions>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="hint" style={{ marginTop: 12, marginBottom: 0 }}>
        {hasActiveFilter
          ? `Gösterilen: ${items.length} / Toplam: ${totalCount}`
          : `Toplam: ${totalCount}`}
      </p>
    </>
  );
}

interface ProductImageCellProps {
  item: TenantMenuItem;
  broken: boolean;
  onError: () => void;
}

// A compact indicator for the product's primary image: a thumbnail when the
// stored URL loads, a plain "Yok" badge when no image is set, and a distinct
// "Yüklenemedi" badge when a stored URL fails to load — never a broken-image
// icon.
function ProductImageCell({ item, broken, onError }: ProductImageCellProps) {
  if (item.image_url === "") {
    return <Badge variant="danger">Yok</Badge>;
  }
  if (broken) {
    return <Badge variant="danger">Yüklenemedi</Badge>;
  }

  // The stored alt text is optional; the product name is the better fallback.
  const altText =
    item.image_alt_text.trim() !== "" ? item.image_alt_text : item.name;

  return (
    <img
      className="row-thumb"
      src={item.image_url}
      alt={altText}
      loading="lazy"
      decoding="async"
      onError={onError}
    />
  );
}

function describe(err: unknown, fallback: string): string {
  // TenantApiError.message carries the backend's curated public error_message,
  // never a raw cause or response body.
  if (err instanceof TenantApiError) {
    return err.message;
  }
  return fallback;
}
